import React, { useState } from 'react';

import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button, { IconButton } from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem } from '@atlaskit/dropdown-menu';
import ErrorIcon from '@atlaskit/icon/core/migration/error';
import MoreIcon from '@atlaskit/icon/core/migration/show-more-horizontal--more';
// WARNING: there is deprecation warning for inline dialog
import InlineDialog from '@atlaskit/inline-dialog';
import InlineEdit from '@atlaskit/inline-edit';
import Lozenge from '@atlaskit/lozenge';
import { type ValueType } from '@atlaskit/select';
import { R400 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import {
	FireScreenAnalytics,
	fireTrackAnalytics,
	fireUIAnalytics,
} from '@atlassian/analytics-bridge';
import { isMaxCharacterCount, isRequired, useValidators } from '@atlassian/eoc-common';
import { Flex } from '@atlassian/eoc-flex';
import { defaultRegistry } from '@atlassian/eoc-rss-helpers';
import {
	EXTRA_SELECT_PROPS,
	commonMessages as scheduleCommonMessages,
	type TimezoneOption,
	TimezoneSelect,
	useTimezoneOptions,
} from '@atlassian/eoc-schedule-common';

import { SCHEDULE_NAME_MAX_LENGTH } from '../../../../common/constants';
import { messages as commonMessages } from '../../../../common/messages';
import { PageModalType } from '../../../../common/types';
import { useStep } from '../../../../controllers/edit-store/helpers';
import { EditStep } from '../../../../controllers/edit-store/state/types';
import { usePartialUpdateScheduleService } from '../../../../controllers/list';
import { useActions as usePageActions } from '../../../../controllers/page-store';
import { DiscardVariant } from '../../../../controllers/page-store/state/types';
import { store as scheduleStore } from '../../../../controllers/schedule-store';
import { useHasDeleted, useSchedule } from '../../../../controllers/schedule-store/helpers';
import { noLeadingOrTrailingSpaces } from '../../../../utils/validators';

import { messages } from './messages';
import {
	Container,
	DialogError,
	DoneWrapper,
	NameEditView,
	NameReadView,
	NameWrapper,
	TimeZoneEditView,
	TimeZoneReadView,
	timeZoneSelectStyles,
	TimeZoneWrapper,
} from './styled';
import { type SelectOptions } from './types';

const Header = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intlApi = useIntl();
	const [schedule] = useSchedule();
	const [step] = useStep();
	const [hasDeleted] = useHasDeleted();
	const { closePageModal, openDeleteSchedule, openDiscardChanges } = usePageActions();

	const [edittingName, setEdittingName] = useState(false);
	const nameValidator = useValidators([
		isRequired(),
		isMaxCharacterCount(SCHEDULE_NAME_MAX_LENGTH),
		noLeadingOrTrailingSpaces(),
	]);

	const { getTimezoneOption } = useTimezoneOptions();

	const { id: scheduleId, name, timezone: timeZone, enabled } = schedule;

	const { partialUpdate: updateName, isLoading: isNameLoading } = usePartialUpdateScheduleService(
		scheduleId,
		'name',
	);
	const { partialUpdate: updateTimezone, isLoading: isTimezoneLoading } =
		usePartialUpdateScheduleService(scheduleId, 'timezone');
	const { partialUpdate: updateEnabled, isLoading: isEnabledLoading } =
		usePartialUpdateScheduleService(scheduleId, 'enabled');

	const timezoneOption = getTimezoneOption({ timeZone });

	const isSettingsLoading = isEnabledLoading;

	return (
		<Container>
			<NameWrapper expanded={edittingName}>
				<InlineEdit
					defaultValue={name}
					onConfirm={(value?: string) => {
						if (value !== name) {
							const event = createAnalyticsEvent({});
							updateName(value, {
								onSuccess: () => {
									fireTrackAnalytics(event, 'scheduleNameUpdate succeeded');
								},
								onError: () => {
									fireTrackAnalytics(event, 'scheduleNameUpdate failed');
								},
							});
						}

						setEdittingName(false);
					}}
					onEdit={() => setEdittingName(true)}
					onCancel={() => setEdittingName(false)}
					editView={({ errorMessage, ...fieldProps }) => (
						<InlineDialog
							isOpen={fieldProps.isInvalid}
							content={<div>{errorMessage}</div>}
							placement="right"
						>
							<NameEditView
								{...fieldProps}
								autoComplete="off"
								autoFocus
								isCompact
								isDisabled={isNameLoading}
								aria-label={intlApi.formatMessage(messages.editScheduleName)}
								elemAfterInput={
									fieldProps.isInvalid && (
										<DialogError>
											<ErrorIcon
												spacing="spacious"
												label="error"
												color={token('color.icon.danger', R400)}
											/>
										</DialogError>
									)
								}
							/>
						</InlineDialog>
					)}
					readView={() => <NameReadView isLoading={isNameLoading}>{name}</NameReadView>}
					validate={nameValidator}
					editButtonLabel={intlApi.formatMessage(messages.editScheduleName)}
				/>
			</NameWrapper>
			<TimeZoneWrapper alignedRight={edittingName}>
				<InlineEdit<ValueType<TimezoneOption>>
					defaultValue={timezoneOption}
					editView={(fieldProps) => (
						<TimeZoneEditView>
							<TimezoneSelect
								{...EXTRA_SELECT_PROPS}
								{...fieldProps}
								autoFocus
								isDisabled={isTimezoneLoading}
								openMenuOnFocus
								aria-label={intlApi.formatMessage(messages.editScheduleTimezone)}
								styles={{
									control: (base: any) => ({
										...base,
										// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
										...timeZoneSelectStyles,
									}),
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
									...EXTRA_SELECT_PROPS.styles,
								}}
							/>
						</TimeZoneEditView>
					)}
					hideActionButtons
					readView={() => (
						<TimeZoneReadView isLoading={isTimezoneLoading}>
							{timezoneOption.label}
						</TimeZoneReadView>
					)}
					onConfirm={(value: SelectOptions) => {
						if (value.value !== timeZone) {
							const event = createAnalyticsEvent({});
							// FIX: Update schedule timezone offset as well. See: TARDIS-9135
							updateTimezone(value.value, {
								onSuccess: () => {
									fireTrackAnalytics(event, 'timeZoneUpdate succeeded');
								},
								onError: () => {
									fireTrackAnalytics(event, 'timeZoneUpdate failed');
								},
							});
						}
					}}
					editButtonLabel={intlApi.formatMessage(messages.editScheduleTimezone)}
				/>
			</TimeZoneWrapper>
			<Flex columnGap={1} justifyContent="center">
				{hasDeleted && (
					<Lozenge appearance="removed">
						{intlApi.formatMessage(scheduleCommonMessages.missingResponder)}
					</Lozenge>
				)}

				{!enabled && (
					<Lozenge appearance="moved">{intlApi.formatMessage(scheduleCommonMessages.off)}</Lozenge>
				)}
			</Flex>
			<DoneWrapper>
				<Button
					id="done"
					appearance="default"
					onClick={(_, analyticsEvent) => {
						fireUIAnalytics(analyticsEvent, 'editModeClose');

						if (step === EditStep.List) {
							defaultRegistry.getStore(scheduleStore, scheduleId).actions.getTimelineAction();

							closePageModal(PageModalType.EditMode);
						}

						if (step === EditStep.Detail) {
							openDiscardChanges({
								variant: DiscardVariant.Close,
								id: 'done',
							});
						}
					}}
				>
					{intlApi.formatMessage(scheduleCommonMessages.close)}
				</Button>
			</DoneWrapper>
			<DropdownMenu<HTMLButtonElement>
				trigger={({ triggerRef, ...props }) => (
					<IconButton
						{...props}
						label={intlApi.formatMessage(messages.moreActions)}
						id="edit-options-button"
						isLoading={isSettingsLoading}
						appearance="subtle"
						icon={MoreIcon}
						ref={triggerRef}
					/>
				)}
			>
				{enabled && (
					<DropdownItem
						onClick={() => {
							const event = createAnalyticsEvent({});
							fireUIAnalytics(event, 'button clicked', 'editModeDisableSchedule');

							updateEnabled(false);
						}}
					>
						{intlApi.formatMessage(commonMessages.turnOff)}
					</DropdownItem>
				)}
				{!enabled && (
					<DropdownItem
						onClick={() => {
							const event = createAnalyticsEvent({});
							fireUIAnalytics(event, 'button clicked', 'editModeEnableSchedule');

							updateEnabled(true);
						}}
					>
						{intlApi.formatMessage(commonMessages.turnOn)}
					</DropdownItem>
				)}

				<DropdownItem
					onClick={() => {
						const event = createAnalyticsEvent({});
						fireUIAnalytics(event, 'button clicked', 'editModeDeleteSchedule');

						openDeleteSchedule({
							id: ['edit-options-button', 'create-schedule-button'],
						});
					}}
				>
					{intlApi.formatMessage(commonMessages.delete)}
				</DropdownItem>
			</DropdownMenu>
			<FireScreenAnalytics componentName="editSchedule" />
		</Container>
	);
};

export { Header };
